import React from 'react'
import { Link } from 'react-router-dom'
import { AiOutlineHeart } from 'react-icons/ai'
import { FaBolt, FaLayerGroup, FaRegStar, FaStar, FaStore } from 'react-icons/fa'
import { productImage, PLACEHOLDER_IMAGE } from '../../utilies/imageUrl'
import { getCategoryLabel } from '../../utilies/categoryLabels'
import { getListingPromotionBadges, getPromotionFrameClass } from '../../utilies/promotionBadges'
import { resolveProductPrice } from '../../utilies/productPrice'

const stripHtml = (value = '') => String(value)
  .replace(/<[^>]+>/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()

const formatPrice = (value) => {
  const price = Number(value)
  if (!Number.isFinite(price)) return ''
  // Pul həmişə 2 onluq rəqəmlə: 5.5 → "5,50" (formatlama, dizayn dəyişmir)
  return `₼${price.toLocaleString('az-AZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const badgeClass = {
  weekly: 'border-amber-200 bg-gradient-to-r from-amber-400 to-orange-500 text-white',
  showcase: 'border-violet-300 bg-gradient-to-r from-violet-700 to-fuchsia-600 text-white',
  marketplace: 'border-indigo-300 bg-gradient-to-r from-blue-600 to-violet-600 text-white',
  search: 'border-indigo-500 bg-gradient-to-r from-indigo-600 to-violet-600 text-white',
  discount: 'border-rose-200 bg-gradient-to-r from-rose-600 to-orange-500 text-white',
  highlight: 'border-violet-200 bg-violet-600 text-white shadow-violet-300/50',
  bumped: 'border-violet-200 bg-white/95 text-violet-700'
}

const getProductCategory = (product = {}) => {
  if (!product.category) return product.productType === 'physical' ? 'Elan' : 'Digital'
  return getCategoryLabel(product.category)
}

// Satıcı adı əvvəlcə backend-in normalizə etdiyi product.seller obyektindən
// oxunur (kart və detal eyni mənbəni görür); fallback heç vaxt başqa ad deyil.
const getSellerName = (product = {}) => (
  product.seller?.shopName ||
  product.seller?.name ||
  product.shopName ||
  product.sellerName ||
  product.sellerId?.shopInfo?.shopName ||
  'Satıcı məlum deyil'
)

const isDigitalProduct = (product = {}) => (
  product.productType === 'normal' ||
  product.serviceType === 'digital' ||
  product.hasPackages ||
  product.deliveryType === 'automatic'
)

const ItemzoProductCard = ({
  product,
  source,
  boost,
  onWishlist,
  onCart,
  onTrack,
  className = ''
}) => {
  if (!product) return null

  const promotionBadges = getListingPromotionBadges(product, { source })
  const category = getProductCategory(product)
  const seller = getSellerName(product)
  const isDigital = isDigitalProduct(product)
  const isAutomatic = product.deliveryType === 'automatic'
  const stockCount = isAutomatic ? (product.availableStockCount ?? product.stock ?? 0) : product.stock
  const hasStock = Number(stockCount) > 0
  const rating = Number(product.rating) || 0
  const hasRating = rating > 0
  const priceInfo = resolveProductPrice(product)
  const discountedPrice = priceInfo.effectivePrice
  const originalPrice = priceInfo.originalPrice
  const discountPercent = priceInfo.discountPercent

  const track = () => {
    if (onTrack) onTrack(product, source, boost)
  }

  const wishlist = (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (onWishlist) onWishlist(product)
  }

  return (
    <article
      data-doping-card={boost ? 'true' : 'false'}
      data-product-id={product._id}
      data-boost-id={boost?.id || ''}
      data-boost-type={boost?.type || ''}
      data-source={source || ''}
      className={`group flex h-[320px] min-w-0 flex-col overflow-hidden rounded-[14px] border bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${getPromotionFrameClass(product, { source })} ${className}`}
    >
      <div className='relative h-[138px] w-full shrink-0 overflow-hidden bg-slate-100'>
        <Link
          to={`/product/details/${product.slug}`}
          onClick={track}
          aria-label={stripHtml(product.name || 'Məhsul')}
          className='block h-full w-full'
        >
          <img
            key={product._id}
            src={productImage(product)}
            alt={`${stripHtml(product.name || 'Məhsul')} - Itemzo`}
            loading='lazy'
            decoding='async'
            width='240'
            height='138'
            onError={(e) => {
              if (!e.currentTarget.src.endsWith(PLACEHOLDER_IMAGE)) e.currentTarget.src = PLACEHOLDER_IMAGE
            }}
            className='h-full w-full object-cover transition-transform duration-300 group-hover:scale-105'
          />
        </Link>

        {promotionBadges.length > 0 && (
          <div className='absolute left-2 top-2 z-10 flex max-w-[78%] flex-wrap gap-1' aria-label='Elan promosyonları'>
            {promotionBadges.map(badge => (
              <span
                key={badge.key}
                className={`inline-flex h-6 max-w-full items-center rounded-full border px-2.5 text-[10px] font-bold leading-none shadow-sm ${badgeClass[badge.tone] || badgeClass.bumped}`}
              >
                <span className='truncate'>{badge.label}</span>
              </span>
            ))}
          </div>
        )}

        <button
          type='button'
          onClick={wishlist}
          aria-label='Favorilərə əlavə et'
          className='absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full border border-white/80 bg-white/95 text-slate-500 shadow-sm transition-colors hover:bg-brand-600 hover:text-white'
        >
          <AiOutlineHeart size={15} />
        </button>

        {isDigital && (
          <span className='absolute bottom-2 left-2 z-10 inline-flex h-6 items-center gap-1 rounded-full bg-white/95 px-2 text-[10px] font-bold text-slate-700 shadow-sm'>
            <FaLayerGroup size={8} className='text-brand-600' />
            Digital
          </span>
        )}
      </div>

      <div className='flex min-h-0 flex-1 flex-col gap-1.5 p-3'>
        <div className='flex min-w-0 items-center gap-1.5'>
          <span className='flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700'>
            <FaStore size={8} />
          </span>
          <span className='truncate text-[11px] font-semibold text-slate-700'>{seller}</span>
          <span className='h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400' />
        </div>

        <p className='truncate text-[10px] font-semibold uppercase text-slate-400'>
          {category}
        </p>

        <Link to={`/product/details/${product.slug}`} onClick={track} className='block'>
          <h2 className='line-clamp-2 min-h-[34px] text-[13px] font-semibold leading-[17px] text-slate-900 transition-colors group-hover:text-brand-700'>
            {stripHtml(product.name)}
          </h2>
        </Link>

        <div className='flex min-h-[20px] items-center justify-between gap-1.5 overflow-hidden'>
          <span className='inline-flex min-w-0 items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700'>
            {hasRating ? <FaStar size={9} /> : <FaRegStar size={9} />}
            <span>{hasRating ? rating.toFixed(1) : 'Yeni'}</span>
          </span>
          <span className={`truncate rounded-full px-2 py-0.5 text-[10px] font-bold ${hasStock ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-600'}`}>
            {hasStock ? (isAutomatic ? `Stok ${stockCount}` : 'Stokda var') : 'Stok yoxdur'}
          </span>
          {isAutomatic && (
            <span className='hidden shrink-0 items-center gap-1 rounded-full bg-cyan-50 px-2 py-0.5 text-[10px] font-bold text-cyan-700 sm:inline-flex'>
              <FaBolt size={8} /> Avto
            </span>
          )}
        </div>

        <div className='mt-auto'>
          <div className='flex min-h-[22px] flex-wrap items-end gap-1.5'>
            <span className='text-[17px] font-bold leading-none text-brand-700'>{formatPrice(discountedPrice)}</span>
            {discountPercent > 0 && (
              <span className='text-[11px] leading-none text-slate-400 line-through'>{formatPrice(originalPrice)}</span>
            )}
          </div>
          <button
            type='button'
            onClick={() => onCart && onCart(product)}
            disabled={!hasStock}
            className={`mt-2 flex h-8 w-full items-center justify-center rounded-lg text-xs font-bold transition-colors ${
              hasStock
                ? 'bg-gradient-to-r from-brand-600 to-teal-500 text-white hover:from-brand-700 hover:to-teal-600'
                : 'cursor-not-allowed bg-slate-100 text-slate-400'
            }`}
          >
            İndi al
          </button>
        </div>
      </div>
    </article>
  )
}

export default ItemzoProductCard
